@if (auftrag$ | async; as auftrag) {
  <soft-page>
    <soft-page-header>
      <div class="flex flex-col w-full gap-1">
        <h6 class="text-base font-bold">
          {{ '#TRANSPORT.ORDER_DETAIL.TITLE_PREFIX' | translate }} {{ auftrag?.nummer }}
        </h6>
        <div class="flex flex-row items-center justify-between w-full">
          <div class="flex flex-row items-center gap-2">
            <i class="fa-regular fa-truck text-sm"></i>
            <span class="text-sm">
              {{ auftrag?.lkw?.bezeichnung }} {{ auftrag?.lkw?.kennzeichen }}
            </span>
          </div>
          @if (auftrag?.lieferdatum) {
            <span class="text-sm">
              {{ $safeNavigationMigration(auftrag?.lieferdatum) | date: 'dd.MM.yyyy' }}
            </span>
          }
        </div>
      </div>
    </soft-page-header>

    <!-- Gesamtmenge -->
    <section class="mb-6 mt-4">
      <h6 class="soft-overline mb-2">
        {{ '#TRANSPORT.ORDER_DETAIL.TOTAL_AMOUNT' | translate }}
      </h6>
      <div class="soft-card rounded-m p-3 gap-2 flex flex-col">
        @if (auftrag?.positionen) {
          @for (item of auftrag.positionen; track item; let last = $last) {
            <div
              class="flex flex-row justify-between items-center flex-nowrap"
              [class.border-b]="!last"
              [class.pb-2]="!last"
            >
              <div class="flex flex-col justify-center">
                <strong>{{ item?.artikel?.bezeichnung || 'Keine Bezeichnung' }}</strong>
                <span class="text-sm color-light">
                  {{ '#TRANSPORT.ORDER_DETAIL.ITEM_NUMBER_PREFIX' | translate }}
                  {{ item?.artikel?.artikelnummer }}
                </span>
              </div>
              <span class="shrink-0 ml-4">
                {{ item?.menge }} {{ item?.arteh?.arteh }}
              </span>
            </div>
          }
        } @else {
          @for (_ of [1, 2]; track $index; let last = $last) {
            <div
              class="flex flex-row justify-between items-center flex-nowrap"
              [class.border-b]="!last"
              [class.pb-2]="!last"
            >
              <div class="flex flex-col gap-1">
                <soft-loading-skeleton
                  style="width: 12rem; height: 1rem"
                />
                <soft-loading-skeleton
                  style="width: 8rem; height: 0.75rem"
                />
              </div>
              <soft-loading-skeleton
                style="width: 4rem; height: 1rem"
              />
            </div>
          }
        }
      </div>
    </section>

    @if (auftrag?.vermerk) {
      <section class="mb-6">
        <h6 class="soft-title color-tertiary mb-2">
          {{ '#TRANSPORT.ORDER_DETAIL.BELEGVERMERK_OVERLINE' | translate }}
        </h6>
        <div class="soft-card rounded-m p-3 text-sm">
          {{ auftrag?.vermerk }}
        </div>
      </section>
    }

    <!-- Stationen -->
    <section class="mb-6">
      <h6 class="soft-overline mb-2!">
        {{ '#TRANSPORT.ORDER_DETAIL.STATION_OVERLINE' | translate }}
      </h6>
      @if ((stationen$ | async)?.length; as _count) {
        @for (
          station of stationen$ | async;
          track station;
          let i = $index;
          let last = $last
        ) {
          <div class="flex flex-row gap-3">
            <!-- Timeline -->
            <div class="flex flex-col items-center pl-1">
              @if (i > 0) {
                <div class="w-0.5 flex-1 bg-border-primary/30"></div>
              } @else {
                <div class="flex-1"></div>
              }
              <div
                class="flex items-center justify-center w-7 h-7 rounded-full border-2 shrink-0 my-1"
                [class.border-success-500]="station?.idkopflf"
                [class.bg-success-50]="station?.idkopflf"
                [class.border-primary]="!station?.idkopflf"
              >
                @if (station?.idkopflf) {
                  <i class="fa-solid fa-check text-success-500 text-xs"></i>
                }
              </div>
              @if (!last) {
                <div class="w-0.5 flex-1 bg-border-primary/30"></div>
              } @else {
                <div class="flex-1"></div>
              }
            </div>
            <!-- Card -->
            <div
              class="soft-card rounded-m overflow-hidden relative mb-3 cursor-pointer flex-1 transition-colors hover:bg-hover p-0!"
              (click)="
                !station?.idkopflf
                  ? openStationDetails(station, 'edit')
                  : openStationDetails(station, 'readonly')
              "
            >
              <div
                class="absolute top-0 bottom-0 left-0 w-1.5"
                [class.bg-primary]="!station?.idkopflf"
                [class.bg-success-500]="station?.idkopflf"
              ></div>
              <div
                class="flex flex-row justify-between items-center flex-nowrap p-3 pl-4"
              >
                <div class="flex flex-col justify-center flex-1">
                  <div
                    class="flex flex-row items-baseline justify-between mb-1 gap-2 flex-nowrap"
                  >
                    <div class="flex flex-row items-baseline gap-2 shrink-0">
                      <span class="color-light soft-overline whitespace-nowrap">
                        {{ '#TRANSPORT.ORDER_DETAIL.STATION_PREFIX' | translate }}
                        {{ i + 1 }}
                      </span>
                      @if (station.idkopflf) {
                        <span
                          class="bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300 text-[10px] font-semibold uppercase leading-none px-1.5 py-1 rounded whitespace-nowrap"
                        >
                          {{ '#TRANSPORT.ORDER_DETAIL.COMPLETED' | translate }}
                        </span>
                      }
                      @if (station | stationChanged | async) {
                        <span
                          class="bg-warning-100 text-warning-700 dark:bg-warning-900 dark:text-warning-300 text-[10px] font-semibold uppercase leading-none px-1.5 py-1 rounded whitespace-nowrap"
                        >
                          {{ '#TRANSPORT.ORDER_DETAIL.CHANGED' | translate }}
                        </span>
                      }
                    </div>
                    <span class="color-light soft-overline whitespace-nowrap shrink-0">
                      {{ '#TRANSPORT.ORDER_DETAIL.AUFTRAG_PREFIX' | translate }}
                      {{ station.belegnummer }}
                    </span>
                  </div>
                  <div class="flex flex-row items-center gap-2 mb-1">
                    @if (
                      (station?.lieferadressen?.name1 &&
                        station?.lieferadressen?.name1 !== '') ||
                      (station?.lieferadressen?.name2 &&
                        station?.lieferadressen?.name2 !== '')
                    ) {
                      <strong>
                        {{
                          station?.lieferadressen?.name1 || station?.lieferadressen?.name2
                        }}
                      </strong>
                    } @else {
                      <strong>
                        {{ '#TRANSPORT.ORDER_DETAIL.NO_STATION_NAME' | translate }}
                      </strong>
                    }
                  </div>
                  <span class="text-sm color-light">
                    {{ station?.lieferadressen?.postleitzahl }}
                    {{ station?.lieferadressen?.ort }}.
                    {{ station?.lieferadressen?.strasse }}
                  </span>
                </div>
                @if (station?.idkopflf) {
                  <div
                    class="flex items-center justify-center w-10 h-10 rounded bg-success-500 hover:bg-success-600 transition-colors ml-2 shrink-0 cursor-pointer"
                    role="button"
                    (click)="$event.stopPropagation(); printAuftrag(auftrag, station)"
                  >
                    <i class="fa-regular fa-print text-sm text-white"></i>
                  </div>
                } @else {
                  <div
                    class="flex items-center justify-center w-10 h-10 rounded-full hover:bg-hover transition-colors ml-2 shrink-0"
                  >
                    <i class="fa-regular fa-chevron-right text-sm color-lighter"></i>
                  </div>
                }
              </div>
            </div>
          </div>
        }
      } @else {
        @for (_ of [1, 2]; track $index; let last = $last) {
          <div class="flex flex-row gap-3">
            <div class="flex flex-col items-center pl-1">
              @if ($index > 0) {
                <div class="w-0.5 flex-1 bg-border-primary/30"></div>
              } @else {
                <div class="flex-1"></div>
              }
              <div
                class="flex items-center justify-center w-7 h-7 rounded-full border-2 border-primary shrink-0 my-1"
              ></div>
              @if (!last) {
                <div class="w-0.5 flex-1 bg-border-primary/30"></div>
              } @else {
                <div class="flex-1"></div>
              }
            </div>
            <div class="soft-card rounded-m overflow-hidden relative mb-3 flex-1 p-0!">
              <div class="absolute top-0 bottom-0 left-0 w-1.5 bg-primary"></div>
              <div class="flex flex-row justify-between items-center p-3 pl-4">
                <div class="flex flex-col gap-2 flex-1">
                  <div class="flex flex-row justify-between">
                    <soft-loading-skeleton
                      style="width: 5rem; height: 0.625rem"
                    />
                    <soft-loading-skeleton
                      style="width: 8rem; height: 0.625rem"
                    />
                  </div>
                  <soft-loading-skeleton
                    style="width: 40%; height: 1rem"
                  />
                  <soft-loading-skeleton
                    style="width: 55%; height: 0.75rem"
                  />
                </div>
                <div class="w-10 h-10 ml-2 shrink-0"></div>
              </div>
            </div>
          </div>
        }
      }
    </section>
  </soft-page>
} @else if (loading$ | async) {
  <soft-page>
    <!-- Header skeleton -->
    <soft-page-header>
      <div class="flex flex-col w-full gap-1">
        <soft-loading-skeleton
          style="width: 60%; height: 1.1rem"
        />
        <div class="flex flex-row items-center justify-between w-full">
          <soft-loading-skeleton
            style="width: 10rem; height: 0.875rem"
          />
          <soft-loading-skeleton
            style="width: 5rem; height: 0.875rem"
          />
        </div>
      </div>
    </soft-page-header>

    <!-- Gesamtmenge skeleton -->
    <section class="mb-6 mt-4">
      <h6 class="soft-overline mb-2">
        {{ '#TRANSPORT.ORDER_DETAIL.TOTAL_AMOUNT' | translate }}
      </h6>
      <div class="soft-card rounded-m p-3 flex flex-col gap-2">
        @for (_ of [1, 2]; track $index; let last = $last) {
          <div
            class="flex flex-row justify-between items-center flex-nowrap"
            [class.border-b]="!last"
            [class.pb-2]="!last"
          >
            <div class="flex flex-col gap-1">
              <soft-loading-skeleton
                style="width: 12rem; height: 1rem"
              />
              <soft-loading-skeleton
                style="width: 8rem; height: 0.75rem"
              />
            </div>
            <soft-loading-skeleton
              style="width: 4rem; height: 1rem"
            />
          </div>
        }
      </div>
    </section>

    <!-- Stationen skeleton -->
    <section class="mb-6">
      <h6 class="soft-overline mb-2">
        {{ '#TRANSPORT.ORDER_DETAIL.STATION_OVERLINE' | translate }}
      </h6>
      @for (_ of [1, 2]; track $index; let last = $last) {
        <div class="flex flex-row gap-3">
          <div class="flex flex-col items-center pl-1">
            @if ($index > 0) {
              <div class="w-0.5 flex-1 bg-border-primary/30"></div>
            } @else {
              <div class="flex-1"></div>
            }
            <div
              class="flex items-center justify-center w-7 h-7 rounded-full border-2 border-primary shrink-0 my-1"
            ></div>
            @if (!last) {
              <div class="w-0.5 flex-1 bg-border-primary/30"></div>
            } @else {
              <div class="flex-1"></div>
            }
          </div>
          <div class="soft-card rounded-m overflow-hidden relative mb-3 flex-1 p-0!">
            <div class="absolute top-0 bottom-0 left-0 w-1.5 bg-primary"></div>
            <div class="flex flex-row justify-between items-center p-3 pl-4">
              <div class="flex flex-col gap-2 flex-1">
                <div class="flex flex-row justify-between">
                  <soft-loading-skeleton
                    style="width: 5rem; height: 0.625rem"
                  />
                  <soft-loading-skeleton
                    style="width: 8rem; height: 0.625rem"
                  />
                </div>
                <soft-loading-skeleton
                  style="width: 40%; height: 1rem"
                />
                <soft-loading-skeleton
                  style="width: 55%; height: 0.75rem"
                />
              </div>
              <div class="w-10 h-10 ml-2 shrink-0"></div>
            </div>
          </div>
        </div>
      }
    </section>
  </soft-page>
}
